import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, LockKey, DownloadSimple, FileArrowDown } from "@phosphor-icons/react";
import api from "@/lib/api";
import { downloadFilesAsZip } from "@/lib/zip";
import PageMeta from "@/components/PageMeta";
import PageShell from "@/components/PageShell";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

export default function Download() {
  const { code: routeCode } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(routeCode || "");
  const [transfer, setTransfer] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [dl, setDl] = useState({ id: null, pct: 0, label: "" });

  useEffect(() => {
    if (routeCode && routeCode.length === 6) lookup(routeCode);
     
  }, [routeCode]);

  const lookup = async (c) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/transfers/by-code/${c}`);
      setTransfer(data);
      setNeedsPassword(data.has_password && (!data.files || data.files.length === 0));
    } catch (e) {
      setTransfer(null);
      const s = e?.response?.status;
      if (s === 404) toast.error("Code not found");
      else if (s === 410) toast.error("Transfer expired");
      else toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/transfers/by-code/${code}/verify`, { password });
      setTransfer(data); setNeedsPassword(false);
      toast.success("Unlocked");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Wrong password" : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchOne = async (fileId, filename, onProgress) => {
    try {
      const resp = await api.get(`/transfers/by-code/${code}/download/${fileId}`, {
        params: transfer?.has_password ? { password } : {},
        responseType: "blob",
        onDownloadProgress: (e) => {
          if (e.total) onProgress?.(e.loaded, e.total);
          else onProgress?.(e.loaded, 0);
        },
      });
      const blobUrl = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return true;
    } catch {
      return false;
    }
  };

  const download = async (f) => {
    if (dl.id) return;
    setDl({ id: f.id, pct: 0, label: f.filename });
    const ok = await fetchOne(f.id, f.filename, (loaded, total) => {
      const pct = total ? Math.round((loaded / total) * 100) : 0;
      setDl((s) => ({ ...s, pct }));
    });
    if (!ok) toast.error("Download failed");
    setDl({ id: null, pct: 0, label: "" });
  };

  const downloadAll = async () => {
    if (dl.id || !transfer?.files?.length) return;
    const files = transfer.files;
    setDl({ id: "all", pct: 0, label: `Preparing ${files.length} files…` });
    const items = files.map((f) => ({
      url: `/transfers/by-code/${code}/download/${f.id}`,
      params: transfer?.has_password ? { password } : undefined,
      filename: f.filename,
      size: f.size,
    }));
    const res = await downloadFilesAsZip(items, `sendme-${code}`, (pct, label) => {
      setDl({ id: "all", pct, label });
    });
    setDl({ id: null, pct: 0, label: "" });
    if (res.ok) toast.success(`Zipped ${files.length} files`);
    else toast.error(`Download failed${res.failedAt ? `: ${res.failedAt}` : ""}`);
  };

  return (
    <PageShell
      eyebrow="Receive"
      title={<>Enter a code. <span className="text-[#10B981]">Get your files.</span></>}
      subtitle="Type the 6-digit code you were given. If the sender set a password, you’ll need that too."
    >
      <PageMeta
        title="Receive a file · Type a 6-digit code"
        description="Received a SendMe code? Enter your 6-digit transfer code to download the file. No signup required — works on any browser, mobile or desktop."
      />
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
        <div className="brut-card p-6">
          <div className="small-caps mb-3">Transfer code</div>
          <div className="flex gap-2">
            <input
              data-testid="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              className="input-brut w-full font-mono text-2xl tracking-[0.4em] text-center"
            />
            <button
              data-testid="lookup-btn"
              disabled={code.length !== 6 || loading}
              onClick={() => { navigate(`/r/${code}`); lookup(code); }}
              className="btn-brut-secondary disabled:opacity-40"
            >
              <ArrowRight size={18} />
            </button>
          </div>

          {needsPassword && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6">
              <div className="small-caps mb-2 flex items-center gap-2"><LockKey size={14}/> Password</div>
              <div className="flex gap-2">
                <input
                  type="password"
                  data-testid="download-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-brut w-full"
                  placeholder="Enter password"
                />
                <button data-testid="verify-btn" onClick={verify} disabled={!password || loading} className="btn-brut-primary disabled:opacity-40">
                  Unlock
                </button>
              </div>
            </motion.div>
          )}
        </div>

        <div>
          {!transfer ? (
            <div className="border border-dashed border-black/20 p-10 text-center text-black/50">
              <FileArrowDown size={32} className="mx-auto mb-3 text-[#10B981]" weight="duotone"/>
              <div className="small-caps">Awaiting code</div>
            </div>
          ) : needsPassword ? (
            <div className="border border-black p-10 text-center" data-testid="locked-state">
              <LockKey size={32} className="mx-auto mb-3 text-[#FF3366]" weight="duotone"/>
              <div className="text-lg font-bold">Password-protected</div>
              <div className="text-sm text-black/55 mt-1">Enter the password to view files.</div>
            </div>
          ) : (
            <div className="brut-card p-6" data-testid="files-panel">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="small-caps">{transfer.file_count} file{transfer.file_count > 1 ? "s" : ""} · {fmtBytes(transfer.total_size)}</div>
                {transfer.files.length > 1 && (
                  <button
                    onClick={downloadAll}
                    disabled={dl.id !== null}
                    data-testid="download-all"
                    className="btn-brut-primary !py-2 !px-3 text-xs disabled:opacity-40"
                  >
                    <DownloadSimple size={14}/> Download all
                  </button>
                )}
              </div>

              {dl.id === "all" && (
                <div className="mb-4 p-3 border border-black/15 bg-black/[0.02]" data-testid="download-all-progress">
                  <div className="flex items-center justify-between text-xs font-mono mb-1.5">
                    <span className="truncate text-black/70">{dl.label}</span>
                    <span className="text-[#10B981]">{dl.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-black/10">
                    <div className="h-1.5 bg-[#10B981] transition-all" style={{ width: `${dl.pct}%` }}/>
                  </div>
                </div>
              )}

              <ul className="divide-y divide-black/10">
                {transfer.files.map((f) => {
                  const isDl = dl.id === f.id;
                  return (
                    <li key={f.id} className="py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{f.filename}</div>
                          <div className="text-xs text-black/50 font-mono">{fmtBytes(f.size)}</div>
                        </div>
                        <button
                          onClick={() => download(f)}
                          disabled={dl.id !== null}
                          data-testid={`download-${f.id}`}
                          className="btn-brut-secondary !py-2 !px-3 disabled:opacity-40"
                        >
                          <DownloadSimple size={16}/> {isDl ? `${dl.pct}%` : "Download"}
                        </button>
                      </div>
                      {isDl && (
                        <div className="h-1 bg-black/10 mt-2" data-testid={`progress-${f.id}`}>
                          <div className="h-1 bg-[#10B981] transition-all" style={{ width: `${dl.pct}%` }}/>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 text-xs text-black/45 font-mono">Expires {new Date(transfer.expires_at).toLocaleString()}</div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
